import React from "react";
import PropTypes from "prop-types";
import { BaseEdge } from "reactflow";

const CustomOrgEdge = ({ sourceX, sourceY, targetX, targetY, data, style = {}, markerEnd }) => {
  // CONFIGURATION
  const parentStub = 30; // How far down before turning
  const r = 7; // Corner Radius
  const childCount = data?.childCount || 0;

  let path = "";

  // 1. CONDITION: Straight below
  // This handles the middle child in a set of 3
  const isStraightBelow = Math.abs(sourceX - targetX) < 2;

  if (isStraightBelow) {
    path = `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
  }

  // 2. CONDITION: Small Teams (2 or 3 children)
  // If there are 3 children, and this isn't the middle one (checked above),
  // we draw a simple "Step" line without gutter logic.
  else if (childCount <= 3) {
    const dir = targetX > sourceX ? 1 : -1;

    path = `M ${sourceX} ${sourceY} `;
    path += `v ${parentStub - r} `; // Down
    path += `q 0 ${r} ${r * dir} ${r} `; // Curve
    path += `H ${targetX - r * dir} `; // Horizontal to child X
    path += `q ${r * dir} 0 ${r * dir} ${r} `; // Curve
    path += `L ${targetX} ${targetY}`; // Down to child
  }

  // 3. CONDITION: Complex Layout (4+ children)
  // Use Gutter logic to avoid overlapping many sibling cards
  else {
    const branchHeight = 30;
    const maxColWidth = data?.maxColumnWidth || 180;
    const dynamicOffset = maxColWidth / 2 + 20;
    const isRight = targetX > sourceX;
    const gutterX = targetX + (isRight ? -dynamicOffset : dynamicOffset);

    path += `M ${sourceX} ${sourceY} `;
    path += `v ${parentStub} `;

    const dirToGutter = gutterX > sourceX ? 1 : -1;
    if (Math.abs(gutterX - sourceX) > r) {
      path += `H ${gutterX - r * dirToGutter} `;
      path += `q ${r * dirToGutter} 0 ${r * dirToGutter} ${r} `;
    } else {
      path += `L ${gutterX} ${sourceY + parentStub + r} `;
    }

    const verticalLegY = targetY - branchHeight;
    const dist = verticalLegY - (sourceY + parentStub + r);

    if (dist > 0) {
      path += `v ${dist} `;
    } else {
      path += `V ${verticalLegY} `;
    }

    const dirToChild = targetX > gutterX ? 1 : -1;
    path += `q 0 ${r} ${r * dirToChild} ${r} `;
    path += `H ${targetX - r * dirToChild} `;
    path += `q ${r * dirToChild} 0 ${r * dirToChild} ${r} `;
    path += `L ${targetX} ${targetY}`;
  }

  return (
    <BaseEdge
      path={path}
      markerEnd={markerEnd}
      style={{ ...style, strokeLinecap: "round", strokeLinejoin: "round" }}
    />
  );
};

CustomOrgEdge.propTypes = {
  sourceX: PropTypes.number.isRequired,
  sourceY: PropTypes.number.isRequired,
  targetX: PropTypes.number.isRequired,
  targetY: PropTypes.number.isRequired,
  style: PropTypes.object,
  markerEnd: PropTypes.string,
  data: PropTypes.shape({
    maxColumnWidth: PropTypes.number,
    childCount: PropTypes.number, // Pass this from your layout engine
  }),
};

export default CustomOrgEdge;
